import {
  ExposureStatusEnum,
  ExposureTypeClientEnum,
  FilterClauseType,
  IconName,
  cleanupInitialFilterDateRange,
  useCurrenciesFilter,
  useTradingMarketAccountsFilter,
  type BlotterTableFilter,
  type CreditBlotterExposure,
  type FilterClause,
  type FilterableProperty,
  type UsePersistedBlotterTable,
} from '@talos/kyoko';
import { compact, isEqual, keys, values } from 'lodash-es';
import { useCallback, useMemo, useState, type SetStateAction } from 'react';
import type { CreditTableFilter } from './types';

export interface UseCreditFilterParams {
  persistedBlotterTable: UsePersistedBlotterTable;
}
export function useCreditFilter({ persistedBlotterTable }: UseCreditFilterParams) {
  const { initialState, onFilterChanged: saveFilter } = persistedBlotterTable;
  const [filter, setFilter] = useState<CreditTableFilter>(() => cleanupInitialFilterDateRange(initialState?.filter));

  const changeFilter = useCallback(
    (action: SetStateAction<BlotterTableFilter>) => {
      const priorFilter = filter;
      const newFilter = action instanceof Function ? action(filter) : action;

      if (!isEqual(priorFilter, newFilter)) {
        setFilter(newFilter);
        saveFilter(newFilter);
      }
    },
    [filter, saveFilter]
  );

  const tradingMarketAccountsFilter = useTradingMarketAccountsFilter();
  const currenciesFilter = useCurrenciesFilter();

  const filterableProperties = useMemo(
    () =>
      compact<FilterableProperty<keyof CreditTableFilter>>([
        {
          key: 'Statuses',
          label: 'Statuses',
          control: 'single-select',
          icon: IconName.StatusOnline,
          options: Object.keys(ExposureStatusEnum),
          getOptionLabel: status => status,
        },
        {
          key: 'Types',
          label: 'Type',
          control: 'multi-select',
          icon: IconName.Puzzle,
          options: values(ExposureTypeClientEnum) satisfies CreditBlotterExposure['type'][],
          getOptionLabel: type => type,
        },
        { ...tradingMarketAccountsFilter, key: 'Accounts' },
        currenciesFilter,
      ]),
    [tradingMarketAccountsFilter, currenciesFilter]
  );

  const initialFilterClauses = useMemo(() => {
    const clauses: FilterClause[] = [];
    if (filter) {
      (keys(filter) as (keyof CreditTableFilter)[]).forEach((key: keyof CreditTableFilter) => {
        clauses.push({
          key: key,
          type: FilterClauseType.INCLUSIVE,
          selections: filter[key] as string[],
        });
      });
    }
    return clauses;
  }, [filter]);

  return {
    filter,
    filterableProperties,
    initialFilterClauses,
    changeFilter,
  };
}

export function colIDToFilterBuilderKey(id: string): keyof CreditTableFilter | undefined {
  switch (id as keyof CreditBlotterExposure) {
    case 'MarketAccount':
      return 'Accounts';
    case 'type':
      return 'Types';
    case 'status':
      return 'Statuses';
  }

  if (id === 'Currency') {
    return 'Currencies';
  }

  return undefined;
}
